import {
  ApplicationMethod,
  Product,
  ProductQuantityType,
  Property,
  TankInformation,
  TimingEvent,
  Weather,
  WeatherData,
  WeatherMode,
  WorkType
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { AREA_TYPE_REPORTED } from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import {
  Applicator,
  FormValues,
  Product as ProductForm,
  RecordSource
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { mapWeatherToBackend } from 'hooks/useWeatherApi';
import { AxiosError, AxiosRequestConfig, CancelToken } from 'axios';
import { getAccessToken } from 'utilities/base-auth';
import {
  SORT_BY_VALUES,
  SORT_ORDER_VALUES,
  OperationSummaryError,
  OperationSummaryParams,
  OperationSummaryResponse,
  SortConfig
} from 'components/RecordsListView/types';
import { CropZoneMatchType } from 'components/RecordsListView/FilterDrawer/Constants/filterFormikData';

export interface Organization {
  id: string;
  [key: string]: any;
}

export interface BasePayloadOptions {
  includeEndDate?: boolean;
  includeWeather?: boolean;
}

export interface BasePayloadBody {
  id?: string;
  orgId: string;
  name: string;
  applicationMethod?: ApplicationMethod;
  type: WorkType;
  cropSeasonIds: string[];
  notes?: string;
  startDate: string;
  endDate?: string;
  timingEvent?: TimingEvent;
  authorization: {
    authorizerId?: string;
    authorizationDate: string;
  };
  totalCost: number;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  tagIds?: string[];
  applicators?: Array<Applicator>;
  properties: Array<Property>;
  equipmentIds?: string[];
  products: Array<Product>;
  tankInformation?: TankInformation;
  weather?: Weather;
  recordSource?: RecordSource;
}

export interface OperationSummaryQueryParams {
  page?: number;
  size?: number;
  fromDate?: string;
  toDate?: string;
  sorts?: SortConfig[];
  filters?: { name?: string };
  id?: string;
  cropZoneIds?: string[];
  cropZoneMatchType?: CropZoneMatchType;
}

const convertProductsForPayload = (products: ProductForm[] = []) => {
  const convertedProducts = products.map(product => ({
    productId: product.id,
    productName: product.name,
    productQuantityType:
      ProductQuantityType[
        product.quantityMode as keyof typeof ProductQuantityType
      ],
    priceValue: product.priceValue,
    priceUnit: product.priceUnit,
    totalCostValue: product.totalCost,
    totalCostUnit: undefined,
    appliedAreaValue: Number(product.appliedAreaValue),
    appliedAreaUnit: 'acre',
    appliedAreaCoveragePercent: product.coveragePercent!,
    totalProductValue: Number(product.totalProduct),
    totalProductUnit: product.totalProductUnit,
    ratePerAreaValue: Number(product.ratePerAcre),
    ratePerAreaUnit: product.ratePerAreaUnit,
    ratePerTankValue: product.ratePerTank || 0,
    ratePerTankUnit: product.ratePerTankUnit || product.stdpackageunit,
    applicationMethod:
      ApplicationMethod[
        product.applicationMethod as keyof typeof ApplicationMethod
      ],
    targetPestNameIds: product.targetPests?.map(x => x.nameId) || undefined,
    registrationNumber:
      product.registrationNumber || product.registrationnumber,
    custom: product.custom,
    isCarrier: product.isCarrier,
    isHidden: product.isHidden
  }));

  const filteredProducts = convertedProducts.filter(
    product => !(product.isCarrier && product.isHidden) // Remove the carrier if it's marked as hidden
  );

  return filteredProducts;
};

/**
 * Creates a base payload body for Applications and WorkOrders
 */
export function createBasePayloadBody(
  organization: Organization,
  formData: FormValues,
  options: BasePayloadOptions
): BasePayloadBody {
  // Extract crop season IDs from form data
  const selectedCropSeasonIds = formData.cropZones.flatMap(
    cropZone => cropZone.cropSeasons?.map(season => season.id) ?? []
  );
  const selectedCropSeasons = [...new Set(selectedCropSeasonIds)];

  const body: BasePayloadBody = {
    id: formData.id,
    orgId: organization.id,
    name: formData.applicationId,
    applicationMethod: formData.applicationMethod
      ? ApplicationMethod[
          formData.applicationMethod as keyof typeof ApplicationMethod
        ]
      : undefined,
    type:
      WorkType[formData.workType as keyof typeof WorkType] || WorkType.general,
    cropSeasonIds: selectedCropSeasons,
    notes: formData.notes || undefined,
    startDate: formData.startDateTime.format(),
    timingEvent: TimingEvent[formData.timingEvent as keyof typeof TimingEvent],
    authorization: {
      authorizerId: formData.authorizedBy?.id || undefined,
      authorizationDate: formData.authorizationDate.format()
    },
    totalCost: formData.totalCost!,
    totalAppliedArea: formData.totalAppliedAcreage,
    totalAppliedAreaUnit: 'acre',
    tagIds: formData.tagIds,
    applicators: formData.applicators?.filter(
      applicator => applicator?.personId || applicator?.companyId
    ),
    properties: formData.cropZones.map(cropZone => ({
      farmId: cropZone.propertyId,
      fieldId: cropZone.fieldId,
      cropZoneId: cropZone.id,
      areaValue: cropZone.appliedAcreage,
      areaUnit: 'acre',
      areaType:
        cropZone.selectedAreaType === AREA_TYPE_REPORTED
          ? 'REPORTED'
          : 'BOUNDARY',
      coveragePercent: cropZone.coveragePercent
    })),
    equipmentIds:
      formData.equipments?.map(equipment => equipment.id) || undefined,
    products: convertProductsForPayload(formData.products),
    tankInformation: formData.isTankInfoEnabled
      ? {
          tankSizeValue: formData.tankSize || 0,
          tankSizeUnit: 'gallon',
          tankCount: formData.tankCount || 0,
          ratePerAcreValue: formData.overallRatePerAcre || 0,
          ratePerAcreUnit: 'gallon',
          totalProductValue: formData.totalProduct || 0,
          totalProductUnit: 'gallon'
        }
      : undefined,
    recordSource: formData.recordSource
  };

  // End date is only included for Applications
  if (options.includeEndDate && formData.endDateTime) {
    body.endDate = formData.endDateTime.format();
  }

  // Weather is only included for Applications
  if (options.includeWeather) {
    const nowUtc = Date.now();
    const areDatesInFuture =
      formData.startDateTime.valueOf() > nowUtc ||
      (formData.endDateTime?.valueOf() ?? 0) > nowUtc;

    body.weather = {
      mode: formData?.weather?.mode || WeatherMode.MANUAL,
      displayAutomaticWeatherModal: areDatesInFuture,
      ...(mapWeatherToBackend(formData.weather) as {
        start: WeatherData;
        end: WeatherData;
      })
    };
  }

  return body;
}

export const getRequestConfig = (
  organizationId: string,
  cancelToken?: CancelToken
): AxiosRequestConfig & { signal?: AbortSignal } => {
  return {
    headers: {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: organizationId
      }),
      Authorization: `Bearer ${getAccessToken()}`
    },
    cancelToken
  };
};

const DEFAULT_SORTS: SortConfig[] = [
  { sortBy: SORT_BY_VALUES.CREATION_DATE, order: SORT_ORDER_VALUES.DESC },
  { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
];

// Maps the "show records matching..." radio choice to the geometric FilterMode
// the proxy/base API use to compare an operation's crop zones against the
// requested cropZoneIds (see CWFWEB-8657 acceptance criteria):
// ANY -> at least one crop zone in common -> intersects
// ALL -> operation's crop zones must be a superset of the selection -> contains
//
// Query-param binding on the proxy resolves FilterMode via Enum.valueOf (case-sensitive,
// matching the enum constant name), NOT the lowercase @JsonValue used for JSON bodies -
// so these must stay uppercase even though the base API's JSON representation is lowercase.
const CROP_ZONE_MATCH_TYPE_TO_FILTER_MODE: Record<CropZoneMatchType, string> = {
  [CropZoneMatchType.ANY]: 'INTERSECTS',
  [CropZoneMatchType.ALL]: 'CONTAINS'
};

/**
 * Builds the query string for operation summary endpoints (applications and work-orders).
 */
export const buildOperationSummaryQueryString = (
  params: OperationSummaryQueryParams,
  selectedCropSeasonIds: string[]
) => {
  const fullParams: OperationSummaryParams = {
    cropSeasonIds: selectedCropSeasonIds,
    page: params.page ?? 0,
    size: params.size ?? 50
  };

  if (params.fromDate) fullParams.fromDate = params.fromDate;
  if (params.toDate) fullParams.toDate = params.toDate;
  if (params.filters?.name) fullParams.name = params.filters.name;
  if (params.id) fullParams.id = params.id;
  if (params.cropZoneIds?.length) fullParams.cropZoneIds = params.cropZoneIds;

  const sorts = params.sorts ?? DEFAULT_SORTS;
  const searchParams = new URLSearchParams();

  (Object.keys(fullParams) as Array<keyof OperationSummaryParams>).forEach(
    key => {
      if (key === 'filters' || key === 'sorts') return;

      let value = fullParams[key];

      if (value === undefined || value === null) return;

      if (typeof value === 'number') {
        value = String(value);
      }

      if (Array.isArray(value)) {
        value = value.join(',');
      }

      searchParams.append(key, value);
    }
  );

  // filterModes is a Map<String, FilterMode> on the proxy, bound from
  // query params using Spring's indexed map syntax: filterModes[key]=value
  if (params.cropZoneIds?.length && params.cropZoneMatchType) {
    const filterMode =
      CROP_ZONE_MATCH_TYPE_TO_FILTER_MODE[params.cropZoneMatchType];
    if (filterMode) {
      searchParams.append('filterModes[cropZoneIds]', filterMode);
    }
  }

  sorts.forEach(sort => {
    searchParams.append('sort', `${sort.sortBy},${sort.order}`);
  });

  return searchParams.toString();
};

export function handleOperationSummaryError<T>(
  error: AxiosError
): { data: OperationSummaryResponse<T> } {
  const errorData = error.response?.data as
    | {
        displayErrorMessage?: string;
        message?: string;
        code?: string;
        timestamp?: string;
      }
    | undefined;

  const errors: OperationSummaryError = {
    message: String(errorData?.displayErrorMessage ?? errorData?.message ?? ''),
    code: String(errorData?.code ?? ''),
    timestamp: String(errorData?.timestamp ?? '')
  };

  const hasErrorPayload =
    errors.message !== '' || errors.code !== '' || errors.timestamp !== '';

  return {
    data: {
      results: [],
      totalElements: 0,
      totalPages: 0,
      size: 0,
      currentPage: 0,
      numberOfElements: 0,
      errors: hasErrorPayload ? errors : null
    }
  };
}
